import { useTranslation } from 'react-i18next';

const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center min-h-screen bg-brand-background text-brand-brown p-4 pt-8 dark:bg-gray-900 dark:text-gray-300">
      <div className="w-full max-w-4xl space-y-8">
        <header>
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-green dark:text-gray-100">
            {t('about.learn_more', 'Learn more')}
          </h1>
        </header>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t(
              'about.welcome_title',
              'Welcome to Tulasi Home - Print & Artisan Production Studio'
            )}
          </h2>
          <p>
            {t(
              'about.intro_p1',
              'We are Tulasi Home. Our studio was born out of love for art and the desire to preserve ' +
                'its authenticity in every detail. For us, printing is not just a technical process, but ' +
                'a continuation of the artist’s creativity.'
            )}
          </p>
          <p>
            {t(
              'about.intro_p2',
              'We know your concerns because we live them ourselves:'
            )}
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('about.intro_b1', 'When the paper texture brings watercolor to life.')}</li>
            <li>
              {t(
                'about.intro_b2',
                'When an order is placed in minutes without endless messaging.'
              )}
            </li>
            <li>
              {t(
                'about.intro_b3',
                'When the packaging guarantees the work arrives intact and beautiful.'
              )}
            </li>
          </ul>
          <p>
            {t(
              'about.intro_p3',
              'That’s why we combined a love for art with attention to technology.'
            )}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('about.care_title', 'Tulasi Home is more than printing. It’s care.')}
          </h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              {t(
                'about.care_b1',
                'We guarantee quality and color longevity because we believe in the value of your work.'
              )}
            </li>
            <li>
              {t(
                'about.care_b2',
                'We personally check every print before it goes to the client.'
              )}
            </li>
            <li>
              {t(
                'about.care_b3',
                'We give you freedom of materials — from premium Epson and Hahnemühle to optimal choices for everyday tasks.'
              )}
            </li>
            <li>
              {t(
                'about.care_b4',
                'We handle logistics and delivery so you can focus on creativity.'
              )}
            </li>
          </ul>
          <p>
            {t(
              'about.care_p',
              'Tulasi Home is a space where art is respected and you feel confident in the result. We print with love, attention, and uncompromising quality.'
            )}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('about.tech_title', 'Our technologies and standards')}
          </h2>
          <p>
            {t(
              'about.tech_p1',
              'We trust only proven world‑class solutions.'
            )}
          </p>
          <p>
            {t(
              'about.tech_p2',
              'Our studio uses the Epson SureColor SC‑P7500 — a professional printer chosen by leading artists worldwide for museum‑quality printing. It reproduces the finest shades and details, making every print as close as possible to the original.'
            )}
          </p>
          <p>
            {t(
              'about.tech_p3',
              'The printer runs on original Epson UltraChrome Pro12 pigment inks. They provide deep blacks, smooth gradients, and rich colors that last for over 100 years.'
            )}
          </p>
          <p>
            {t(
              'about.tech_p4',
              'For printing, we use Hahnemühle and Epson Fine Art papers — benchmarks in fine art printing. At the same time, we also offer carefully selected mid‑range and economy materials so every artist can choose without compromise.'
            )}
          </p>
          <p>
            {t(
              'about.tech_p5',
              'Every print undergoes manual inspection: we look at color accuracy, paper cleanliness, and printing perfection. This is not just control — it’s a sign of our respect for your art.'
            )}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('about.philosophy_title', 'Our philosophy')}
          </h2>
          <p>
            {t(
              'about.philosophy_p1',
              'Tulasi Home is not a factory, but a creative workshop. We combine technology with love for art. We value not only speed, but also the feeling that each work has received the level of attention it deserves.'
            )}
          </p>
          <p>
            {t('about.philosophy_p2', 'We work for artists who value:')}
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              {t('about.philosophy_b1', 'consistent quality in every run,')}
            </li>
            <li>{t('about.philosophy_b2', 'flexibility in the choice of materials,')}</li>
            <li>{t('about.philosophy_b3', 'care for delivery and packaging,')}</li>
            <li>
              {t(
                'about.philosophy_b4',
                'support from a real person, not an automated system.'
              )}
            </li>
          </ul>
          <p>
            {t(
              'about.philosophy_p3',
              'We want your art to inspire and live long.'
            )}
          </p>
          <p>
            {t(
              'about.philosophy_p4',
              'Together we create Tulasi Home — a space where art and technology, heart and precision unite. We are not a faceless service; we are those who understand your concerns and rejoice in your achievements.'
            )}
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('about.road_title', 'Our path forward')}
          </h2>
          <p>
            {t(
              'about.road_p1',
              'We started with the most reliable foundation: a professional Epson SureColor SC‑P7500 printer and a team that knows what true quality means. This is only the beginning of our journey.'
            )}
          </p>
          <p>
            {t(
              'about.road_p2',
              'We see Tulasi Home as a space that will grow with you — artists, photographers, and creative studios. Thanks to your projects and trust, we will gradually expand our equipment and capabilities, adding new technologies and materials.'
            )}
          </p>
          <p>
            {t(
              'about.road_p3',
              'Our goal is to build a workshop that has everything necessary for any creative task — from intimate watercolor prints to large‑scale projects.'
            )}
          </p>
          <p>
            {t(
              'about.road_p4',
              'We move step by step, preserving warmth and a personal approach, and we believe that with each new work we grow together with you.'
            )}
          </p>
          <h2 className="text-2xl font-semibold text-brand-brown dark:text-gray-100">
            {t('about.together_title', 'Together with you')}
          </h2>
          <p>
            {t(
              'about.together_p1',
              'Tulasi Home is not only about printing, but a story we write together. We believe that art lives when it is passed on — from the artist to the viewer, from heart to heart.'
            )}
          </p>
          <p>
            {t(
              'about.together_p2',
              'Every order is not just a print, but a step in the development of our studio. Thanks to you, we add new materials, master modern technologies, and expand our equipment. Most importantly, we preserve the warmth and trust that make Tulasi Home a living space, not a faceless service.'
            )}
          </p>
          <p>
            {t(
              'about.together_p3',
              'We see our future not as a factory, but as a workshop of co-creation. New print formats will be born here, exhibition projects will take place, and partnerships with artists and photographers will be created. We want Tulasi Home to be a place where art is not only printed, but also inspires, grows, and finds its way into the world.'
            )}
          </p>
          <p>
            {t(
              'about.together_p4',
              'Together we can do more: preserve the authenticity of art in every detail, give it a long life, and build a community where creativity is always supported and respected.'
            )}
          </p>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
